// src/services/authService.js

import { api } from './api.js';

const TOKEN_KEY = 'jwt_token';
const USER_KEY = 'current_user';

let authInitialized = false;
let resolveAuthInitialized;
const authInitializedPromise = new Promise(resolve => {
    resolveAuthInitialized = resolve;
});

// Mantenemos initializeAuth para que CashierPage no falle,
// pero ahora es más simple y se basa en tu lógica.
async function initializeAuth() {
    if (authInitialized) {
        return authInitializedPromise;
    }
    // No es necesario establecer el header aquí. 
    // Se asume que api.js lo toma de localStorage en cada petición.
    authInitialized = true;
    resolveAuthInitialized();
    return authInitializedPromise;
}

export const authService = {
    /**
     * Inicia sesión de un usuario.
     * @param {string} username
     * @param {string} password
     * @returns {Promise<Object>} Objeto con user y token.
     */
    async login(username, password) {
        try {
            const response = await api.post('/auth/login', { username, password });
            
            localStorage.setItem(TOKEN_KEY, response.token);
            localStorage.setItem(USER_KEY, JSON.stringify(response.user));
            
            // ✅ CORRECCIÓN: Se elimina la línea que causaba el error.
            // No se debe modificar el objeto 'api' desde aquí.
            
            window.location.hash = '/cashier';
            return response; // Devolver la respuesta completa
        } catch (error) {
            console.error('Login failed:', error);
            // Re-lanzar para que el componente que llama (LoginPage.js) lo maneje
            throw error; 
        }
    },

    /**
     * Cierra la sesión del usuario.
     */
    logout() {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USER_KEY);
        // No es necesario modificar el objeto 'api' aquí tampoco.
        window.location.hash = '/login';
    },

    /**
     * Obtiene el token JWT almacenado.
     * @returns {string|null} El token o null si no existe.
     */
    getToken() {
        return localStorage.getItem(TOKEN_KEY);
    },

    /**
     * Obtiene la información del usuario actual.
     * @returns {Object|null} Objeto de usuario o null.
     */
    getCurrentUser() {
        const userJson = localStorage.getItem(USER_KEY);
        try {
            return userJson ? JSON.parse(userJson) : null;
        } catch (e) {
            console.error("Error parsing user data from localStorage", e);
            return null;
        }
    },

    /**
     * Verifica si el usuario está autenticado.
     * @returns {boolean} True si hay un token, false en caso contrario.
     */
    isAuthenticated() {
        return !!this.getToken() && !!this.getCurrentUser();
    },

    /**
     * Verifica si el usuario actual tiene uno de los roles permitidos.
     * @param {Array<string>} allowedRoles - Array de roles permitidos (ej. ['admin', 'cashier']).
     * @returns {boolean} True si el usuario tiene un rol permitido, false en caso contrario.
     */
    hasRole(allowedRoles) {
        const user = this.getCurrentUser();
        if (!user || !user.role) {
            return false;
        }
        return allowedRoles.includes(user.role);
    },

    // Se mantiene la función de inicialización para compatibilidad con CashierPage
    initializeAuth,
    authInitializedPromise
};
